import { useCallback, useMemo, useRef, useState } from 'react';
import {
  useChatAttachments,
  useChatDrivePicker,
  useResolvePickGate,
} from '../../../hooks/index.js';
import { useChatApiClient } from '../../../context/index.js';
import { pickGateFromRequest } from '../../../utils/pickGateFromRequest.js';
import { ADD_FROM_DRIVE_MENU_VALUES } from './assistantComposerConstants.js';

/**
 * @param {object} p
 * @param {ReturnType<import('../../../context/index.js').useChat>} p.chat
 * @param {{ warning: (o: object) => void }} p.toast
 * @param {(key: string) => string} p.t
 */
export function useAssistantComposer({ chat, toast, t }) {
  const {
    selectedAgentId,
    conversationId,
    selectedConversationId,
    createNewConversation,
    sendMessage,
    defaultAgentIdForNewChat,
    providers,
    selectedProvider,
    selectedModel,
    onModelChange,
    onProviderChange,
    isStreaming,
    agentIsTyping,
    isCreatingConversation,
    hitlRequest,
    setHitlRequest,
    setCurrentActivity,
    setIsStreaming,
    setAgentIsTyping,
  } = chat;

  const apiClient = useChatApiClient();

  const [draft, setDraft] = useState('');
  const [showDrivePickerModal, setShowDrivePickerModal] = useState(false);
  const [composerMountKey, setComposerMountKey] = useState(0);

  // The workflow resume triggered by resolving a pick gate emits no lifecycle
  // feedback events (only the next gate, after a long delay). Optimistically
  // switch on the working indicator so the user sees progress; the next WS
  // event (input.required / completion) clears or replaces it.
  const handleResumeStart = useCallback(() => {
    setIsStreaming?.(true);
    setAgentIsTyping?.(true);
    setCurrentActivity?.({ status: 'WORKING', kind: 'lifecycle', label: 'Working…' });
  }, [setIsStreaming, setAgentIsTyping, setCurrentActivity]);

  // Symmetric stop for a decision that FAILED to resume WITHOUT the run
  // continuing (invalid input, retryable network / 5xx): no run is coming, so
  // no event will ever clear the optimistic indicator — clear it here. This is
  // deliberately NOT called for already-resolved / resume-conflict: those mean
  // the run already continued, so clearing here would wipe a live working state
  // and unlock the composer while the agent is still running.
  const handleResumeEnd = useCallback(() => {
    setIsStreaming?.(false);
    setAgentIsTyping?.(false);
    setCurrentActivity?.(null);
  }, [setIsStreaming, setAgentIsTyping, setCurrentActivity]);

  const effectiveAgentId = selectedAgentId || defaultAgentIdForNewChat;

  const {
    attachments,
    uploadFile,
    handleFilesSelected,
    handleAttachmentRemove,
    handleAddDocumentsFromDrive,
    clearAttachments,
    clearDriveSourceAttachments,
    handleAddMenuChange,
    handleModeMenuChange,
  } = useChatAttachments({
    agentUuid: effectiveAgentId,
    conversationUuid: conversationId || selectedConversationId || undefined,
    createNewConversation,
  });

  // A pending document-pick gate hijacks composer uploads: a file dropped or
  // picked while it is active must RESOLVE the gate (via file_uuid), not become
  // a normal agent attachment. Any other gate — or none — leaves uploads alone.
  const pendingPickGate = useMemo(
    () => pickGateFromRequest(hitlRequest),
    [hitlRequest],
  );

  const resolvePickGate = useResolvePickGate({
    apiClient,
    setHitlRequest,
    toast,
    t,
    onResumeStart: handleResumeStart,
  });

  // Upload one file through the shared pipeline, then resolve the pick gate with
  // the resulting file_uuid. Used by both the card's "Upload a file" control and
  // the composer's upload-routing branch below.
  const attachFileToPickGate = useCallback(
    async (file, card = pendingPickGate) => {
      if (!file || !card) return false;
      try {
        const result = await uploadFile(file);
        const fileUuid = result?.fileUuid ?? result;
        if (!fileUuid) {
          toast.warning?.({ message: t('chat.hitl.uploadFailed') });
          return false;
        }
        return await resolvePickGate({
          card,
          fileUuid,
          name: result?.name ?? file.name,
          fileType:
            result?.fileType ??
            (file.name?.includes?.('.') ? file.name.split('.').pop() : ''),
        });
      } catch (err) {
        toast.error?.({
          title: t('chat.hitl.failed'),
          message: err?.message ?? String(err),
        });
        return false;
      }
    },
    [pendingPickGate, uploadFile, resolvePickGate, toast, t],
  );

  // Route composer uploads: with a pick gate pending, the first file resolves
  // the gate (the rest are ignored — a pick is a single document); otherwise the
  // upload behaves as a normal chat attachment.
  const handleFilesSelectedRouted = useCallback(
    (files) => {
      const list = files ? Array.from(files) : [];
      if (pendingPickGate && list.length > 0) {
        void attachFileToPickGate(list[0], pendingPickGate);
        return;
      }
      handleFilesSelected(files);
    },
    [pendingPickGate, attachFileToPickGate, handleFilesSelected],
  );

  const drivePickerApi = useChatDrivePicker(showDrivePickerModal);

  const handleAddMenuChangeWithDrive = useCallback(
    (value, itemData) => {
      if (ADD_FROM_DRIVE_MENU_VALUES.includes(value)) {
        setShowDrivePickerModal(true);
      }
      handleAddMenuChange?.(value, itemData);
    },
    [handleAddMenuChange],
  );

  const handleDrivePickerConfirm = useCallback(
    (selectedDocuments) => {
      if (selectedDocuments?.length) {
        handleAddDocumentsFromDrive(selectedDocuments);
      }
      setShowDrivePickerModal(false);
    },
    [handleAddDocumentsFromDrive],
  );

  const handleModeTagCloseForDrive = useCallback(
    (modeValue) => {
      if (ADD_FROM_DRIVE_MENU_VALUES.includes(modeValue)) {
        clearDriveSourceAttachments();
      }
    },
    [clearDriveSourceAttachments],
  );

  const attachmentSendOptions = useMemo(() => {
    const list = attachments || [];
    if (list.some((a) => a.isLoading === true)) {
      return { blocked: true };
    }
    const uuids = list.map((a) => a.fileUuid || a.document_uuid).filter(Boolean);
    const attachmentDetails =
      list.length > 0
        ? list.map((a) => ({
            fileUuid: a.fileUuid || a.document_uuid,
            name: a.name ?? 'Document',
            fileType:
              a.fileType ?? (a.name?.includes?.('.') ? a.name.split('.').pop() : ''),
            isLoading: a.isLoading === true,
          }))
        : undefined;
    return {
      blocked: false,
      fileUuids: uuids.length > 0 ? uuids : undefined,
      attachmentDetails,
    };
  }, [attachments]);

  /** Fresh send context each render — callbacks keep stable identities (refs). */
  const composerSendRef = useRef({});
  composerSendRef.current = {
    selectedConversationId,
    conversationId,
    selectedAgentId,
    defaultAgentIdForNewChat,
    createNewConversation,
    sendMessage,
    toast,
    clearAttachments,
    attachmentSendOptions,
    t,
    providersLength: providers?.length ?? 0,
    selectedProvider,
    selectedModel,
  };

  const deliverConversationMessage = useCallback(async (trimmed, cid, agentId) => {
    const ctx = composerSendRef.current;
    const att = ctx.attachmentSendOptions;
    if (att.blocked) {
      ctx.toast.warning({
        message: ctx.t('chat.waitForAttachments'),
      });
      return;
    }
    const llmConfig =
      ctx.providersLength > 0 && ctx.selectedProvider && ctx.selectedModel
        ? { llm_model: ctx.selectedModel, llm_model_provider: ctx.selectedProvider }
        : undefined;
    try {
      await ctx.sendMessage(trimmed, {
        conversationId: cid,
        agentId,
        ...(llmConfig && { llmConfig }),
        ...(att.fileUuids || att.attachmentDetails
          ? { fileUuids: att.fileUuids, attachmentDetails: att.attachmentDetails }
          : {}),
      });
    } finally {
      ctx.clearAttachments?.();
      setComposerMountKey((k) => k + 1);
    }
  }, []);

  const ensureConversationThenSend = useCallback(async (messageText, agentId) => {
    const trimmed = typeof messageText === 'string' ? messageText.trim() : '';
    if (!trimmed || !agentId) return;

    const ctx = composerSendRef.current;
    const att = ctx.attachmentSendOptions;
    if (att.blocked) {
      ctx.toast.warning({
        message: ctx.t('chat.waitForAttachments'),
      });
      return;
    }

    setDraft('');
    let cid = ctx.selectedConversationId || ctx.conversationId;
    if (!cid && ctx.createNewConversation) {
      try {
        const newConversation = await ctx.createNewConversation(agentId, {
          title: trimmed.slice(0, 40),
          selectAfterCreate: true,
        });
        cid =
          newConversation?.conversationUuid ||
          newConversation?.conversation_uuid ||
          newConversation?.id ||
          newConversation?.uuid;
      } catch (err) {
        console.error('Failed to create conversation:', err);
        return;
      }
    }
    if (!cid) {
      console.error('No conversation UUID available for send');
      return;
    }
    await deliverConversationMessage(trimmed, cid, agentId);
  }, [deliverConversationMessage]);

  const handleEmptySend = useCallback(
    (messageText) => void ensureConversationThenSend(messageText, effectiveAgentId),
    [ensureConversationThenSend, effectiveAgentId],
  );

  const handleAgentSend = useCallback(
    (messageText) => void ensureConversationThenSend(
      messageText,
      selectedAgentId ?? defaultAgentIdForNewChat,
    ),
    [ensureConversationThenSend, selectedAgentId, defaultAgentIdForNewChat],
  );

  const handleConversationSend = useCallback(
    async (messageText) => {
      const trimmed = typeof messageText === 'string' ? messageText.trim() : '';
      if (!trimmed) return;
      const ctx = composerSendRef.current;
      const cid = ctx.conversationId || ctx.selectedConversationId;
      const agentId = ctx.selectedAgentId || ctx.defaultAgentIdForNewChat;
      if (!cid || !agentId) return;

      const att = ctx.attachmentSendOptions;
      if (att.blocked) {
        ctx.toast.warning({
          message: ctx.t('chat.waitForAttachments'),
        });
        return;
      }

      setDraft('');
      await deliverConversationMessage(trimmed, cid, agentId);
    },
    [deliverConversationMessage],
  );

  const modelChatProps = useMemo(
    () =>
      providers?.length > 0
        ? {
            providers,
            modelSelectorValue: selectedModel ?? undefined,
            modelSelectorProvider: selectedProvider ?? undefined,
            onModelChange,
            onProviderChange,
            modelAlignMenu: 'right',
          }
        : {},
    [providers, selectedModel, selectedProvider, onModelChange, onProviderChange],
  );

  const chatInputAddMenuItems = useMemo(
    () => [
      { icon: 'attach_file', text: t('chat.uploadFile'), value: 'upload', showTag: true, tagColor: 'success' },
      {
        icon: 'hardDrive2',
        text: t('drivePicker.addFromAimsDrive'),
        value: 'drive',
      },
    ],
    [t],
  );

  const hasAimsDriveAttachments = useMemo(
    () => attachments.some((a) => a.fromAimsDrive),
    [attachments],
  );

  const handleDriveImportBadgeClose = useCallback(() => {
    clearDriveSourceAttachments();
  }, [clearDriveSourceAttachments]);

  const chatInputFooterBadges = useMemo(() => {
    if (!hasAimsDriveAttachments) return [];
    return [
      {
        id: 'aims-drive-import',
        label: t('drivePicker.addFromAimsDrive'),
        variant: 'success',
        onClose: handleDriveImportBadgeClose,
      },
    ];
  }, [hasAimsDriveAttachments, t, handleDriveImportBadgeClose]);

  const chatInputAttachmentProps = useMemo(
    () => ({
      composerMountKey,
      allowUploadFiles: Boolean(effectiveAgentId),
      attachments,
      onFilesSelected: handleFilesSelectedRouted,
      onAttachmentRemove: handleAttachmentRemove,
      onAddMenuChange: handleAddMenuChangeWithDrive,
      onModeMenuChange: handleModeMenuChange,
      onModeTagClose: handleModeTagCloseForDrive,
      addMenuItems: chatInputAddMenuItems,
      footerBadges: chatInputFooterBadges,
    }),
    [
      composerMountKey,
      effectiveAgentId,
      attachments,
      handleFilesSelectedRouted,
      handleAttachmentRemove,
      handleAddMenuChangeWithDrive,
      handleModeMenuChange,
      handleModeTagCloseForDrive,
      chatInputAddMenuItems,
      chatInputFooterBadges,
    ],
  );

  const processing = Boolean(isStreaming || agentIsTyping || isCreatingConversation);

  return useMemo(() => ({
    draft,
    setDraft,
    showDrivePickerModal,
    setShowDrivePickerModal,
    drivePickerApi,
    handleDrivePickerConfirm,
    effectiveAgentId,
    handleEmptySend,
    handleAgentSend,
    handleConversationSend,
    chatInputAttachmentProps,
    modelChatProps,
    processing,
    attachFileToPickGate,
    resolvePickGate,
    markWorkflowResuming: handleResumeStart,
    clearWorkflowResuming: handleResumeEnd,
  }), [
    draft,
    setDraft,
    showDrivePickerModal,
    setShowDrivePickerModal,
    drivePickerApi,
    handleDrivePickerConfirm,
    effectiveAgentId,
    handleEmptySend,
    handleAgentSend,
    handleConversationSend,
    chatInputAttachmentProps,
    modelChatProps,
    processing,
    attachFileToPickGate,
    resolvePickGate,
    handleResumeStart,
    handleResumeEnd,
  ]);
}
